import React, { useEffect, useState } from 'react';
import useSWR from 'swr';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { ambil, kirim, pesanGalat, denganCabang, base, labelTgl, LABEL_PENGAJUAN } from './bersama';

// Tab Pengajuan: izin / sakit / cuti dari karyawan (setujui / tolak) dan catat langsung oleh admin.

const WARNA = { menunggu: 'warning', disetujui: 'success', ditolak: 'error' };
const LABEL = { menunggu: 'Menunggu', disetujui: 'Disetujui', ditolak: 'Ditolak' };

/** Foto lampiran butuh login: dimuat sebagai blob, bukan alamat publik. */
const Lampiran = ({ nama }) => {
  const [url, setUrl] = useState(null);
  const [buka, setBuka] = useState(false);
  useEffect(() => {
    let alamat = null;
    let batal = false;
    axios.get(`${base()}/absensi/pengajuan/lampiran/${nama}`, { withCredentials: true, responseType: 'blob' })
      .then((r) => { if (!batal) { alamat = URL.createObjectURL(r.data); setUrl(alamat); } })
      .catch(() => {});
    return () => { batal = true; if (alamat) URL.revokeObjectURL(alamat); };
  }, [nama]);
  if (!url) return <Typography variant="caption">memuat…</Typography>;
  return (
    <>
      <Box component="img" src={url} alt="lampiran" onClick={() => setBuka(true)} sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 1, cursor: 'zoom-in' }} />
      <Dialog open={buka} onClose={() => setBuka(false)} maxWidth="md">
        <Box component="img" src={url} alt="lampiran" sx={{ maxWidth: '100%', maxHeight: '85vh', display: 'block' }} onClick={() => setBuka(false)} />
      </Dialog>
    </>
  );
};

const DialogCatat = ({ open, cabang, onTutup, onTersimpan }) => {
  const kosong = { useruuid: '', jenis: 'izin', tanggalMulai: '', tanggalSelesai: '', alasan: '' };
  const [isi, setIsi] = useState(kosong);
  const [galat, setGalat] = useState('');
  const { data } = useSWR(open ? denganCabang('/absensi/jadwal', cabang, { dari: new Date().toISOString().slice(0, 10), sampai: new Date().toISOString().slice(0, 10) }) : null, ambil);
  const karyawan = data?.data || [];
  useEffect(() => { if (open) { setIsi(kosong); setGalat(''); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const simpan = async () => {
    try {
      const r = await kirim('POST', '/absensi/pengajuan', { ...isi, tanggalSelesai: isi.tanggalSelesai || isi.tanggalMulai, cabang: cabang || undefined });
      onTersimpan(r.message);
    } catch (e) {
      setGalat(pesanGalat(e));
    }
  };
  return (
    <Dialog open={open} onClose={onTutup} maxWidth="xs" fullWidth>
      <DialogTitle>Catat izin / sakit / cuti</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        <TextField select label="Karyawan" value={isi.useruuid} onChange={(e) => setIsi({ ...isi, useruuid: e.target.value })}>
          {karyawan.map((k) => <MenuItem key={k.uuid} value={k.uuid}>{k.nama}</MenuItem>)}
        </TextField>
        <TextField select label="Jenis" value={isi.jenis} onChange={(e) => setIsi({ ...isi, jenis: e.target.value })}>
          {Object.entries(LABEL_PENGAJUAN).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
        </TextField>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField type="date" label="Dari" value={isi.tanggalMulai} onChange={(e) => setIsi({ ...isi, tanggalMulai: e.target.value })} InputLabelProps={{ shrink: true }} fullWidth />
          <TextField type="date" label="Sampai" value={isi.tanggalSelesai} onChange={(e) => setIsi({ ...isi, tanggalSelesai: e.target.value })} InputLabelProps={{ shrink: true }} fullWidth />
        </Box>
        <TextField label="Alasan" multiline minRows={2} value={isi.alasan} onChange={(e) => setIsi({ ...isi, alasan: e.target.value })} />
        <Alert severity="info">Dicatat admin langsung disetujui.</Alert>
        {galat && <Alert severity="error">{galat}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onTutup}>Batal</Button>
        <Button variant="contained" onClick={simpan} disabled={!isi.useruuid || !isi.tanggalMulai}>Simpan</Button>
      </DialogActions>
    </Dialog>
  );
};

const TabPengajuan = ({ cabang, onPesan }) => {
  const [status, setStatus] = useState('menunggu');
  const [catat, setCatat] = useState(false);
  const { data, error, mutate } = useSWR(denganCabang('/absensi/pengajuan', cabang, status === 'semua' ? {} : { status }), ambil);
  const daftar = data?.data || [];

  const putuskan = async (p, keputusan) => {
    const { value, isConfirmed } = await Swal.fire({
      title: keputusan === 'disetujui' ? 'Setujui pengajuan?' : 'Tolak pengajuan?',
      input: 'text', inputPlaceholder: 'Catatan (opsional)', showCancelButton: true,
      confirmButtonText: keputusan === 'disetujui' ? 'Setujui' : 'Tolak', cancelButtonText: 'Batal',
    });
    if (!isConfirmed) return;
    try {
      const r = await kirim('PUT', `/absensi/pengajuan/${p.uuid}/putuskan`, { status: keputusan, catatan: value, cabang: cabang || undefined });
      onPesan(r.message); mutate();
    } catch (e) {
      Swal.fire('Gagal', pesanGalat(e), 'error');
    }
  };
  const hapus = async (p) => {
    const ok = await Swal.fire({ title: 'Hapus pengajuan?', icon: 'warning', showCancelButton: true, confirmButtonText: 'Hapus', cancelButtonText: 'Batal' });
    if (!ok.isConfirmed) return;
    try { await kirim('DELETE', `/absensi/pengajuan/${p.uuid}`); mutate(); } catch (e) { Swal.fire('Gagal', pesanGalat(e), 'error'); }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          {['menunggu', 'disetujui', 'ditolak', 'semua'].map((s) => (
            <Chip key={s} clickable color={status === s ? 'primary' : 'default'} onClick={() => setStatus(s)}
              label={s === 'semua' ? 'Semua' : `${LABEL[s]}${s === 'menunggu' && data ? ` (${data.menunggu})` : ''}`} />
          ))}
          <Box sx={{ flex: 1 }} />
          <Button variant="contained" size="small" onClick={() => setCatat(true)}>Catat izin/sakit/cuti</Button>
        </Box>
        {error && <Alert severity="error">{pesanGalat(error)}</Alert>}
        {data && !daftar.length && <Typography color="text.secondary">Tidak ada pengajuan.</Typography>}
        {daftar.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Karyawan</TableCell>
                  <TableCell>Jenis</TableCell>
                  <TableCell>Tanggal</TableCell>
                  <TableCell>Alasan</TableCell>
                  <TableCell>Surat</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {daftar.map((p) => (
                  <TableRow key={p.uuid}>
                    <TableCell>{p.karyawan?.nama || '-'}{p.dibuatAdmin && <Typography variant="caption" color="text.secondary" component="div">dicatat admin</Typography>}</TableCell>
                    <TableCell>{LABEL_PENGAJUAN[p.jenis]}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {labelTgl(p.tanggalMulai, { day: 'numeric', month: 'short' })}
                      {p.tanggalSelesai !== p.tanggalMulai && ` – ${labelTgl(p.tanggalSelesai, { day: 'numeric', month: 'short' })}`}
                      <Typography variant="caption" color="text.secondary" component="div">{p.jumlahHari} hari</Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>{p.alasan}{p.catatanAdmin && <Typography variant="caption" color="text.secondary" component="div">Admin: {p.catatanAdmin}</Typography>}</TableCell>
                    <TableCell>{p.lampiran ? <Lampiran nama={p.lampiran} /> : '-'}</TableCell>
                    <TableCell><Chip size="small" color={WARNA[p.status]} label={LABEL[p.status]} /></TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {p.status === 'menunggu' && (
                        <>
                          <Button size="small" color="success" onClick={() => putuskan(p, 'disetujui')}>Setujui</Button>
                          <Button size="small" color="error" onClick={() => putuskan(p, 'ditolak')}>Tolak</Button>
                        </>
                      )}
                      <Button size="small" onClick={() => hapus(p)}>Hapus</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <DialogCatat open={catat} cabang={cabang} onTutup={() => setCatat(false)} onTersimpan={(m) => { setCatat(false); onPesan(m); mutate(); }} />
      </CardContent>
    </Card>
  );
};

export default TabPengajuan;
